/* eslint-disable react/prop-types */
import qs from 'qs';
import React, { useCallback, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import * as contactModalsSelectors from '@src/containers/ContactsModal/selectors';

import Modal from '@src/components/Modal';
import {
	mediaPreviewItems,
	mediaPreviewIndex,
	isMediaPreviewModalOpened,
	mediaPreviewDetails,
} from '@src/containers/InfoModals/selectors';
import { useActions } from '@src/containers/InfoModals/hooks';
import MediaBigFSCVenue from '@src/components/MediaBigFS/connectors/MediaBigFSVenue';

import MediaPreview from '..';

const MediaPreviewCCommon = withRouter((props) => {
	const {
		targetCode,
		targetType = 'media',
		fullscreen,
		location,
		history,
		...rest
	} = props;
	const { closeMediaPreviewModal, setMediaPreviews, setMediaPreviewsIndex } =
		useActions();
	const mediaIndex = useSelector(mediaPreviewIndex);
	const medias = useSelector(mediaPreviewItems);
	const details = useSelector(mediaPreviewDetails);
	const isOpened = useSelector(isMediaPreviewModalOpened);
	const isNonClosable = useSelector(contactModalsSelectors.isVisible);

	const handleClose = React.useCallback(() => {
		if (!isNonClosable) {
			const q = qs.parse(location.search, { ignoreQueryPrefix: true });
			delete q[targetType];
			history.push({
				search: qs.stringify(q, { addQueryPrefix: true }),
			});
			setMediaPreviews();
			closeMediaPreviewModal();
		}
	}, [
		targetType,
		setMediaPreviews,
		closeMediaPreviewModal,
		isNonClosable,
		location.search,
		history,
	]);

	const handleClickNext = React.useCallback(() => {
		const target = Math.min(medias.length - 1, mediaIndex + 1);
		setMediaPreviewsIndex({ current: target });

		const q = qs.parse(location.search, { ignoreQueryPrefix: true });
		history.replace({
			search: qs.stringify(
				{ ...q, [targetType]: `${targetCode}_${target}` },
				{ addQueryPrefix: true },
			),
		});
	}, [
		targetCode,
		targetType,
		mediaIndex,
		medias.length,
		setMediaPreviewsIndex,
		history,
		location.search,
	]);

	const handleClickPrev = React.useCallback(() => {
		const target = Math.max(0, mediaIndex - 1);
		setMediaPreviewsIndex({ current: target });
		const q = qs.parse(location.search, { ignoreQueryPrefix: true });
		history.replace({
			search: qs.stringify(
				{ ...q, [targetType]: `${targetCode}_${target}` },
				{ addQueryPrefix: true },
			),
		});
	}, [
		targetType,
		targetCode,
		mediaIndex,
		setMediaPreviewsIndex,
		history,
		location.search,
	]);

	const handleKeyDown = useCallback(
		(e) => {
			switch (e.code) {
				case 'ArrowLeft':
					handleClickPrev();
					break;
				case 'ArrowRight':
					handleClickNext();
					break;
				default:
			}
		},
		[handleClickPrev, handleClickNext],
	);

	const neighbours = useMemo(() => {
		const start = Math.max(mediaIndex - 1, 0);
		return medias.slice(start, start + 3);
	}, [medias, mediaIndex]);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);

	if (!medias[mediaIndex]) {
		return null;
	}

	const isNextAvail = medias.length > mediaIndex + 1;
	const isPrevAvail = mediaIndex > 0;

	if (fullscreen) {
		return (
			<MediaBigFSCVenue
				neighbours={neighbours}
				isOpen={isOpened}
				details={details}
				media={medias[mediaIndex]}
				onClose={handleClose}
				onClickPrev={isPrevAvail ? handleClickPrev : undefined}
				onClickNext={isNextAvail ? handleClickNext : undefined}
				{...rest}
			/>
		);
	}

	return (
		<Modal size={960} onClose={handleClose} isOpen={isOpened}>
			<MediaPreview
				media={medias[mediaIndex]}
				onClickNext={isNextAvail ? handleClickNext : undefined}
				onClickPrev={isPrevAvail ? handleClickPrev : undefined}
				{...rest}
			/>
		</Modal>
	);
});

export default MediaPreviewCCommon;
